import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { LoggedInGuard } from 'src/app/guards/logged-in.guard';
import { PermissionGuard } from 'src/app/guards/permission.guard';
import { StartPageGuard } from 'src/app/guards/start-page.guard';

const routes: Routes = [
    {
        path: "",
        pathMatch: "full",
        canActivate: [LoggedInGuard, StartPageGuard],
        children: []
    },
    {
        path: "connect-account",
        loadChildren: () => import('./pages/connect-account/connect-account.module').then( m => m.ConnectAccountPageModule),
    },
    {
        path: "oauth-callback",
        loadChildren: () => import('./pages/oauth-callback/oauth-callback.module').then( m => m.OauthCallbackPageModule),
    },
    {
        path: 'switch-account',
        loadChildren: () => import('./pages/switch-account/switch-account.module').then( m => m.SwitchAccountPageModule),
    },
    { 
        path: "disconnect-account",
        loadChildren: () => import('./pages/disconnect-account/disconnect-account.module').then( m => m.DisconnectAccountPageModule),
    },
    { 
        path: "reset",
        loadChildren: () => import('./pages/reset/reset.module').then( m => m.ResetPageModule),
    },


    {
        path: "secure",
        children: [
            {
                path: "",
                pathMatch: "full",
                canActivate: [LoggedInGuard, StartPageGuard],
                children: []
            },
            {
                path: "no-permissions",
                loadChildren: () => import('./pages/secure/no-permissions/no-permissions.module').then( m => m.NoPermissionsPageModule),
                canActivate: [LoggedInGuard],
            },
            {
                path: "sell",
                loadChildren: () => import('./pages/secure/sell/sell.module').then( m => m.SellPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-sell',
                    permissionMessage: "You don't have permission to access the sell screen."
                }
            },
            {
                path: "checkout",
                loadChildren: () => import('./pages/secure/checkout/checkout.module').then( m => m.CheckoutPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-sell-checkout',
                    permissionMessage: "You don't have permission to access the checkout screen."
                }
            },
            {
                path: "settings",
                loadChildren: () => import('./pages/secure/settings/settings.module').then( m => m.SettingsPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-settings',
                    permissionMessage: "You don't have permission to view settings."
                }
            },
            {
                path: "reports",
                children:[
                    {
                        path: "",
                        loadChildren: () => import('./pages/secure/reports/reports.module').then( m => m.ReportsPageModule),
                        canActivate: [LoggedInGuard, PermissionGuard],
                        data: {
                            permission: 'pos-reports',
                            permissionMessage: "You don't have permission to view reports."
                        }
                    },
                    {
                        path: "payments-and-refunds",
                        loadChildren: () => import('./pages/secure/reports/payments-and-refunds/payments-and-refunds.module').then( m => m.PaymentsAndRefundsPageModule),
                        canActivate: [LoggedInGuard, PermissionGuard],
                        data: {
                            permission: 'pos-reports-payments-and-refunds',
                            permissionMessage: "You don't have permission to view the payments and refunds report."
                        }
                    }
                ]
            },
            {
                path: "order-confirmation",
                loadChildren: () => import('./pages/secure/order-confirmation/order-confirmation.module').then( m => m.OrderConfirmationPageModule),
                canActivate: [LoggedInGuard],
            },
            {
                path: "order-pending",
                loadChildren: () => import('./pages/secure/order-pending/order-pending.module').then( m => m.OrderPendingPageModule),
                canActivate: [LoggedInGuard],
            },
            {
                path: "cash-tracking",
                loadChildren: () => import('./pages/secure/cash-tracking/cash-tracking.module').then( m => m.CashTrackingPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-cash-tracking',
                    permissionMessage: "You don't have permission to view cash tracking."
                }
            },
            {
                path: "orders",
                loadChildren: () => import('./pages/secure/orders/orders.module').then( m => m.OrdersPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-orders-view-order',
                    permissionMessage: "You don't have permission to view orders."
                }
            },
            {
                path: "orders/:id",
                loadChildren: () => import('./pages/secure/orders/order/order.module').then( m => m.OrderPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-orders-view-order',
                    permissionMessage: "You don't have permission to view an order."
                }
            },
            {
				path: "orders/:order_id/fulfillment/:fulfillment_id",
				loadChildren: () => import('./pages/secure/orders/order/fulfillment/fulfillment.module').then(m => m.FulfillmentPageModule),
				canActivate: [LoggedInGuard],
			},
            {
				path: "orders/:order_id/refund/:refund_id",
				loadChildren: () => import('./pages/secure/orders/order/refund/refund.module').then(m => m.RefundPageModule),
				canActivate: [LoggedInGuard],
			},
            {
				path: "orders/:order_id/refund/:refund_id/payment/:payment_id",
				loadChildren: () => import('./pages/secure/orders/order/refund/payment/payment.module').then(m => m.PaymentPageModule),
				canActivate: [LoggedInGuard],
			},
            {
				path: "orders/:order_id/return/:return_id",
				loadChildren: () => import('./pages/secure/orders/order/return/return.module').then(m => m.ReturnPageModule),
				canActivate: [LoggedInGuard],
			},
            {
				path: "orders/:order_id/payment/:payment_id",
				loadChildren: () => import('./pages/secure/orders/order/payment/payment.module').then(m => m.PaymentPageModule),
				canActivate: [LoggedInGuard],
			}, 
            {
                path: "organisations",
                loadChildren: () => import('./pages/secure/organisations/organisations.module').then( m => m.OrganisationsPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-organisations-view-organisation',
                    permissionMessage: "You don't have permission to view organisations."
                }
            },
            {
                path: "organisations/:id",
                loadChildren: () => import('./pages/secure/organisations/organisation/organisation.module').then( m => m.OrganisationPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-organisations-view-organisation',
                    permissionMessage: "You don't have permission to view an organisation."
                }
            },
            {
				path: "organisations/:organisation_id/people",
                loadChildren: () => import('./pages/secure/people/people.module').then( m => m.PeoplePageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-people-view-person',
                    permissionMessage: "You don't have permission to view people."
                }
			},
            {
				path: "organisations/:organisation_id/orders",
                loadChildren: () => import('./pages/secure/orders/orders.module').then( m => m.OrdersPageModule),
				canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-orders-view-order',
                    permissionMessage: "You don't have permission to view orders."
                }
			},
            {
            	path: "organisations/:organisation_id/shipping-addresses",
                loadChildren: () => import('./pages/secure/organisations/organisation/shipping-addresses/shipping-addresses.module').then( m => m.ShippingAddressesPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-organisations-shipping-addresses-view-shipping-address',
                    permissionMessage: "You don't have permission to view shipping addresses."
                }
            },
            {
            	path: "organisations/:organisation_id/shipping-addresses/:shipping_address_id",
                loadChildren: () => import('./pages/secure/organisations/organisation/shipping-addresses/shipping-address/shipping-address.module').then( m => m.ShippingAddressPageModule),
            	canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-organisations-shipping-addresses-view-shipping-address',
                    permissionMessage: "You don't have permission to view shipping addresses."
                }
            },
            {
                path: "people",
                loadChildren: () => import('./pages/secure/people/people.module').then( m => m.PeoplePageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-people-view-person',
                    permissionMessage: "You don't have permission to view people."
                }
            },
            {
            	path: "people/:id",
                loadChildren: () => import('./pages/secure/people/person/person.module').then( m => m.PersonPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-people-view-person',
                    permissionMessage: "You don't have permission to view a person."
                }
            },
            { 
            	path: "people/:person_id/orders",
                loadChildren: () => import('./pages/secure/orders/orders.module').then( m => m.OrdersPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-orders-view-order',
                    permissionMessage: "You don't have permission to view orders."
                }
            },
            {
            	path: "people/:person_id/shipping-addresses",
                loadChildren: () => import('./pages/secure/people/person/shipping-addresses/shipping-addresses.module').then( m => m.ShippingAddressesPageModule),
            	canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-people-shipping-addresses-view-shipping-address',
                    permissionMessage: "You don't have permission to view shipping addresses."
                }
            },
            {
            	path: "people/:person_id/shipping-addresses/:shipping_address_id",
                loadChildren: () => import('./pages/secure/people/person/shipping-addresses/shipping-address/shipping-address.module').then( m => m.ShippingAddressPageModule),
            	canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-people-shipping-addresses-view-shipping-address',
                    permissionMessage: "You don't have permission to view a shipping address."
                }
            },
            {
            	path: "people/:id/organisations",
                loadChildren: () => import('./pages/secure/organisations/organisation/organisation.module').then( m => m.OrganisationPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-organisations-view-organisation',
                    permissionMessage: "You don't have permission to view organisations."
                }
            }, 
            {
                path: "parked-carts",
                loadChildren: () => import('./pages/secure/parked-carts/parked-carts.module').then( m => m.ParkedCartsPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-parked-carts-view-parked-cart',
                    permissionMessage: "You don't have permission to view parked carts."
                }
            },
            {
            	path: "parked-carts/:id",
                loadChildren: () => import('./pages/secure/parked-carts/parked-cart/parked-cart.module').then( m => m.ParkedCartPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-parked-carts-view-parked-cart',
                    permissionMessage: "You don't have permission to view a parked cart."
                }
            },
            {
                path: "quotes",
                loadChildren: () => import('./pages/secure/quotes/quotes.module').then( m => m.QuotesPageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-quotes-view-quote',
                    permissionMessage: "You don't have permission to view quotes."
                }
            },
            {
            	path: "quotes/:id",
                loadChildren: () => import('./pages/secure/quotes/quote/quote.module').then( m => m.QuotePageModule),
                canActivate: [LoggedInGuard, PermissionGuard],
                data: {
                    permission: 'pos-quotes-view-quote',
                    permissionMessage: "You don't have permission to view a quote."
                }
            },
        ]
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
    providers: [LoggedInGuard, PermissionGuard, StartPageGuard],
    exports: [RouterModule],
})
export class AppRoutingModule { }
